import {
  defaultPinTheme,
  getPinThemePinComponentSettingsHandlers,
  getPinThemePinSettingsHandlers,
  getPinThemeSettings,
  PinThemePinComponentSettingsHandlerParams,
} from '@lessonup/pin-renderer';
import {
  isDefaultPinTheme,
  Pin,
  PinComponentOfType,
  PinId,
  PinTheme,
  PinThemePinComponentSettingsType,
  TeacherPin,
} from '@lessonup/pins-shared';
import { isNull } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { ActivePinId } from '../EditorContext.types';
import { editorStateStore } from '../store/editorStateStore';

export function useActivePinId() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('activePinId'),
    editorStateStore.getActivePinIdSnapshot,
    editorStateStore.getActivePinIdSnapshot
  );
}

export function useActivePin() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('activePin'),
    editorStateStore.getActivePinSnapshot,
    editorStateStore.getActivePinSnapshot
  );
}

export function useNonNullActivePin() {
  const activePin = useActivePin();
  if (isNull(activePin)) throw new Error('null pin');

  return activePin;
}

export function usePins() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('pins'),
    editorStateStore.getPinsSnapshot,
    editorStateStore.getPinsSnapshot
  );
}

export function useLessonMeta() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('lesson'),
    editorStateStore.getLessonMeta,
    editorStateStore.getLessonMeta
  );
}

export function useSelectedPinIds() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('selectedPinIds'),
    editorStateStore.getSelectedPinIdsSnapshot,
    editorStateStore.getSelectedPinIdsSnapshot
  );
}

export function useEditMode() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('editMode'),
    editorStateStore.getEditModeSnapshot,
    editorStateStore.getEditModeSnapshot
  );
}

export function useSelectedPinComponentIds() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('selectedPinComponentIds'),
    editorStateStore.getSelectedPinComponentIdsSnapshot,
    editorStateStore.getSelectedPinComponentIdsSnapshot
  );
}

export function useSelectedPinComponent() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('selectedPinComponent'),
    editorStateStore.getSelectedPinComponentSnapshot,
    editorStateStore.getSelectedPinComponentSnapshot
  );
}

export function useSubSelectionValue() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('subSelectionValue'),
    editorStateStore.getSubSelectionValueSnapshot,
    editorStateStore.getSubSelectionValueSnapshot
  );
}

export function useCollaborators() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('collaborators'),
    editorStateStore.getCollaboratorsSnapshot,
    editorStateStore.getCollaboratorsSnapshot
  );
}

export function useHighlightedPinComponentId() {
  return useSyncExternalStore(
    editorStateStore.subscribeForType('highlightedPinComponent'),
    editorStateStore.getHighlightedPinComponentIdSnapshot,
    editorStateStore.getHighlightedPinComponentIdSnapshot
  );
}

/**
 * @returns Returns an array of pins containing either the activePin or the activePin plus the selectedPins.
 **/
export function useActivePinSelection() {
  const pins = usePins();
  const activePin = useActivePin();
  const selectedPinIds = useSelectedPinIds();

  if (isNull(activePin)) return [];

  const selectedPins = pins.filter((pin) => selectedPinIds.includes(pin.id));
  return selectedPins.includes(activePin) ? selectedPins : activePin ? [activePin] : [];
}

export function useSetInitialState(activePinId: ActivePinId, pins: TeacherPin[]) {
  // The update should be called immediately during initial render, so we use a ref instead of useEffect
  const firstRun = useRef(true);
  if (!firstRun.current) return;
  firstRun.current = false;
  editorStateStore.setInitialState(activePinId, pins);
}

export function useGetPinThemeByPinId() {
  const pinThemes = useSyncExternalStore(
    editorStateStore.subscribeForType('pins'),
    editorStateStore.getPinThemesSnapshot,
    editorStateStore.getPinThemesSnapshot
  );

  const pins = usePins();

  return useCallback(
    (pinId?: PinId): PinTheme => {
      const pinThemeId = pins.find((pin) => pin.id === pinId)?.settings.pinThemeId;
      if (!pinThemeId) return defaultPinTheme;
      return pinThemes[pinThemeId] ?? defaultPinTheme;
    },
    [pins, pinThemes]
  );
}

export function useLatestPinTheme() {
  const pinThemes = useSyncExternalStore(
    editorStateStore.subscribeForType('pins'),
    editorStateStore.getPinThemesSnapshot,
    editorStateStore.getPinThemesSnapshot
  );

  const latestPinThemeId = useSyncExternalStore(
    editorStateStore.subscribeForType('pins'),
    editorStateStore.getLatestPinThemeIdSnapshot,
    editorStateStore.getLatestPinThemeIdSnapshot
  );

  if (!latestPinThemeId) return;

  return pinThemes[latestPinThemeId];
}

export function usePinThemeForNextPin() {
  const pins = usePins();
  const activePinTheme = useActivePinTheme();
  const activePin = useActivePin();
  const latestPinTheme = useLatestPinTheme();

  if (latestPinTheme && (pins.length === 0 || !activePin)) {
    return latestPinTheme;
  }

  // If default pinTheme, we return undefined
  if (isDefaultPinTheme(activePinTheme)) {
    return;
  }

  return activePinTheme;
}

export function useActivePinTheme() {
  const activePin = useActivePin();
  const getPinThemeByPinId = useGetPinThemeByPinId();

  return getPinThemeByPinId(activePin?.id);
}

export function useActivePinThemeSettings() {
  const activePin = useNonNullActivePin();
  const pinTheme = useActivePinTheme();
  return getPinThemeSettings(pinTheme, activePin.type);
}

export function useEffectiveActivePinComponentSettings<T extends PinThemePinComponentSettingsType>(
  pinComponentType: T,
  params: PinThemePinComponentSettingsHandlerParams<T>
): PinComponentOfType<T>['settings'] {
  const pinThemeSettings = useActivePinThemeSettings();
  const pinTheme = useActivePinTheme();
  const pinSettingsHandlers = useMemo(
    () => getPinThemePinComponentSettingsHandlers(pinThemeSettings, pinTheme.typography),
    [pinThemeSettings, pinTheme]
  );
  return pinSettingsHandlers[pinComponentType](params);
}

export function usePinSettingsHandler<P extends Pin>() {
  const pinTheme = useActivePinTheme();
  const activePin = useNonNullActivePin();

  return getPinThemePinSettingsHandlers(pinTheme)<P>(activePin.type, activePin.settings);
}
